import Dev from '@/munio/dev'
import messageSelector from './messageSelector.js'
import { find } from 'lodash'

let translations = {}

if (typeof Munio !== 'undefined' && Munio.translations) {
  translations = Munio.translations
}

let missingTranslations = {}

function transGet(key) {
  if (typeof key === 'object' && key !== null) {
    if (Object.prototype.hasOwnProperty.call(key, 'default')) {
      return key.default
    }

    if (
      Object.prototype.hasOwnProperty.call(key, window.Munio.config.i18n.key)
    ) {
      return key[window.Munio.config.i18n.key]
    }

    return key[Object.keys(key)[0]] // Fallback to the first key if no specific key is found
  }

  if (translations[key] !== undefined) {
    return translations[key]
  }

  if (
    Dev.enabled &&
    window.Munio.config.i18n.key !== window.Munio.config.languageDefault
  ) {
    if (!Object.prototype.hasOwnProperty.call(missingTranslations, key)) {
      missingTranslations[key] = true
      Dev.symbol('', () => Dev.warn(`Missing translation: "${key}"`))
    }
  }

  return key
}

function transReplace(translation, params = {}) {
  if (typeof translation !== 'string') {
    return translation
  }

  for (let key in params) {
    let value = params[key]

    if (typeof value === 'number') {
      value = value.toString()
    }

    if (typeof value === 'string') {
      translation = translation.replaceAll(new RegExp(':' + key, 'gi'), value)
    }
  }

  return translation
}

/**
 * Translate a string.
 *
 * @param key
 * @param params
 * @returns string
 */
export function trans(key, params = {}) {
  return transReplace(transGet(key), params)
}

/**
 * Translate and pluralize a string.
 *
 * @param key
 * @param num
 * @param params
 * @returns string
 */
export function transChoice(key, num, params = {}) {
  let translation = messageSelector.choose(
    transGet(key),
    num,
    Munio.config.i18n.language,
  )

  if (params.num === undefined) {
    params.num = num
  }

  return transReplace(translation, params)
}

/**
 * Get the flag emoji for a country.
 *
 * @param {string} countryCode
 * @returns {string}
 */
export function flag(countryCode) {
  return countryCode
    .toUpperCase()
    .replaceAll(/./g, (char) =>
      String.fromCodePoint(char.codePointAt(0) + 127397),
    )
}

/**
 * Get the country model
 *
 * @param {string} iso
 */
export function country(iso) {
  return Munio.config.countries.find(
    (country) => country.iso === iso.toLowerCase(),
  )
}

/**
 * Get all the visible country models (sorted)
 *
 * @param {boolean} local
 */
export function countries(local = true) {
  const sortKey = local ? 'i18n' : 'name'

  return Munio.config.countries
    .filter((country) => country.isVisible)
    .sort((a, b) =>
      a[sortKey].localeCompare(b[sortKey], Munio.config.i18n.language),
    )
}

/**
 * Get all the languages (sorted)
 *
 * @param {boolean} local
 * @param {boolean} translated
 * @returns {Array<{
 *  key: string,
 *  language: string,
 *  country: string,
 *  locale: string,
 *  title: string,
 *  name: string,
 *  flag: string,
 *  translated: boolean,
 * }>}
 */
export function languages(local = true, translated = true) {
  const sortKey = local ? 'name' : 'title'

  return Munio.config.languages
    .filter((l) => (translated ? l.translated : true))
    .sort((a, b) =>
      a[sortKey].localeCompare(b[sortKey], Munio.config.i18n.language),
    )
}

/**
 * Language getter
 *
 * @param {string} lang
 * @return object
 */
export function language(lang) {
  return (
    find(Munio.config.languages, { key: lang }) ||
    find(Munio.config.languages, { locale: lang }) ||
    find(Munio.config.languages, { language: lang })
  )
}
